import React, { useEffect, useRef } from 'react';
import { motion, useAnimation, useInView } from 'framer-motion';

interface ScrollRevealProps {
  children: React.ReactNode;
  delay?: number;
  duration?: number;
  direction?: 'up' | 'down' | 'left' | 'right';
  threshold?: number;
}

const ScrollReveal: React.FC<ScrollRevealProps> = ({ 
  children, 
  delay = 0, 
  duration = 0.6,
  direction = 'up',
  threshold = 0.2
}) => {
  const controls = useAnimation();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: threshold });
  const [isMobile, setIsMobile] = React.useState(false);

  React.useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const getInitialPosition = () => {
    // On mobile, always show
    if (isMobile) {
      return { y: 0, opacity: 1, x: 0 };
    }
    
    switch (direction) {
      case 'up': return { y: 60, opacity: 0 };
      case 'down': return { y: -60, opacity: 0 };
      case 'left': return { x: -60, opacity: 0 };
      case 'right': return { x: 60, opacity: 0 };
      default: return { y: 60, opacity: 0 };
    }
  };

  const getFinalPosition = () => {
    switch (direction) {
      case 'up': return { y: 0, opacity: 1 };
      case 'down': return { y: 0, opacity: 1 };
      case 'left': return { x: 0, opacity: 1 };
      case 'right': return { x: 0, opacity: 1 };
      default: return { y: 0, opacity: 1 };
    }
  };

  useEffect(() => {
    if (isMobile) {
      controls.start(getFinalPosition());
      return;
    }
    
    if (isInView) {
      controls.start(getFinalPosition());
    }
  }, [isInView, controls, isMobile]);

  // On mobile, render with visible styles
  if (isMobile) {
    return <div ref={ref} style={{ opacity: 1, transform: 'none' }}>{children}</div>;
  }

  return (
    <motion.div
      ref={ref}
      initial={getInitialPosition()}
      animate={controls}
      transition={{ duration, delay, ease: 'easeOut' }}
    >
      {children}
    </motion.div>
  );
};

export default ScrollReveal;